import MuseFlowAudio from 'Utils/MuseFlowAudioPlugin';
import type { MuseFlowAudioPluginInterface } from 'Utils/MuseFlowAudioPlugin';
import type { AudioBlock, StreamHandle } from './audioStream';

interface NativeMicrophoneFrame {
  samplesBase64: string;
  sampleRate: number;
  timeSec: number;
}

export const decodeNativeFloat32Samples = (samplesBase64: string): Float32Array => {
  const binary = window.atob(samplesBase64);
  if (binary.length === 0 || binary.length % Float32Array.BYTES_PER_ELEMENT !== 0) {
    return new Float32Array(0);
  }

  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new Float32Array(bytes.buffer);
};

export const openNativeMicStream = async (
  onAudio: (block: AudioBlock) => void,
  plugin: MuseFlowAudioPluginInterface = MuseFlowAudio,
): Promise<StreamHandle> => {
  let currentTime = 0;
  let timeOriginSec: number | null = null;
  let stopped = false;
  const listener = await plugin.addListener('microphoneFrame', (frame: NativeMicrophoneFrame) => {
    if (stopped || !Number.isFinite(frame.sampleRate) || !Number.isFinite(frame.timeSec)) return;
    const samples = decodeNativeFloat32Samples(frame.samplesBase64);
    if (samples.length === 0) return;
    timeOriginSec ??= frame.timeSec;
    currentTime = Math.max(0, frame.timeSec - timeOriginSec);
    onAudio({ samples, sampleRate: frame.sampleRate, timeSec: currentTime });
  });

  try {
    await plugin.startMicrophoneCapture();
  } catch (error) {
    await listener.remove();
    throw error;
  }

  return {
    context: {
      get currentTime() {
        return currentTime;
      },
    },
    stop() {
      if (stopped) return;
      stopped = true;
      void listener.remove();
      void plugin.stopMicrophoneCapture();
    },
  };
};
